import React from 'react'
import { Card, Flex, Typography } from 'antd'
import { GlobalOutlined, WhatsAppOutlined } from '@ant-design/icons'
import { Link, LinkProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

type StartChoiceProps = {
    /** Shown below the options, for example a link to the plans. */
    footer?: React.ReactNode
}

/**
 * Asks a new customer what Evaa must do for them, and starts them there: adding their website, or a campaign.
 */
export function StartChoice({ footer }: StartChoiceProps) {
    const { t } = useTranslation()

    const option = (link: LinkProps, icon: React.ReactNode, title: string, text: string) => (
        <Link
            {...link}
            className="flex-1 min-w-[220px]"
        >
            <Card
                hoverable
                className="h-full text-left"
            >
                <Flex
                    vertical
                    gap="small"
                >
                    <span className="text-2xl">{icon}</span>
                    <Typography.Text strong>{title}</Typography.Text>
                    <Typography.Text type="secondary">{text}</Typography.Text>
                </Flex>
            </Card>
        </Link>
    )

    return (
        <Flex
            vertical
            align="center"
            gap="middle"
            className="w-full max-w-[640px] mx-auto"
        >
            <Typography.Title
                level={4}
                className="!mb-0"
            >
                {t('What do you want Evaa to do?')}
            </Typography.Title>
            <Flex
                gap="middle"
                wrap
                className="w-full"
            >
                {option(
                    { to: '/websites', search: { add: true } },
                    <GlobalOutlined />,
                    t('Answer questions on my website'),
                    t("Evaa answers your visitors' questions, based on what is on your website.")
                )}
                {option(
                    { to: '/campaigns/create' },
                    <WhatsAppOutlined />,
                    t('Screen candidates'),
                    t('Evaa talks with candidates on WhatsApp and asks your screening questions.')
                )}
            </Flex>
            {footer}
        </Flex>
    )
}
